
import React from 'react'
import { motion } from 'motion/react'
import { Link } from '@tanstack/react-router'
import { useCanvas } from '@/hooks/useCanvas'
import { useSharedProjects } from '@/providers/ProjectsProvider'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Icon } from '@/components/ui/icon'
import { Button } from '@/components/ui/button'
import { CanvasCard, CanvasCardSkeleton } from '@/components/workspace/card'
import { useCanvasModals } from '@/components/workspace/hooks'
import { CanvasActionModals } from '@/components/workspace/list/CanvasActionModals'
import { CanvasListFooter } from '@/components/workspace/list/CanvasListFooter'

/** Sessions per request. The grid is four wide at its widest, so a page fills whole rows. */
const PAGE_SIZE = 24

export function DraftsPage() {
  const {
    canvases: draftCanvases,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    refetch,
    duplicateCanvas,
    updateCanvas,
    deleteCanvas,
  } = useCanvas({ draftsOnly: true, limit: PAGE_SIZE })

  const { projects } = useSharedProjects()

  const handleRename = React.useCallback(async (canvasId: string, newTitle: string) => {
    await updateCanvas(canvasId, { title: newTitle })
  }, [updateCanvas])

  // Filing a draft under a project takes it out of this list, so re-ask the server
  const handleMove = React.useCallback(async (canvasId: string, projectId: string | null) => {
    await updateCanvas(canvasId, { projectId })
    await refetch()
  }, [updateCanvas, refetch])

  const {
    handleAction,
    closeModal,
    modalHandlers,
    modalProps,
  } = useCanvasModals({
    projects,
    onRename: handleRename,
    onMove: handleMove,
    onDelete: deleteCanvas,
    onDuplicate: duplicateCanvas,
  })

  return (
    <main className="h-full overflow-hidden">
      <div className="h-full overflow-y-auto">
        <div className="w-full space-y-6">
          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load sessions: {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Canvas Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {isLoading
              ? [...Array(12)].map((_, index) => (
                  <CanvasCardSkeleton key={`skeleton-${index}`} />
                ))
              : draftCanvases.map((canvas, index) => (
                  <CanvasCard
                    key={canvas.id}
                    canvas={canvas}
                    contextMenuType="drafts"
                    onAction={handleAction}
                    showProject={false}
                    index={index}
                  />
                ))}
          </div>

          {/* True draft count, plus the next page */}
          {!isLoading && (
            <CanvasListFooter
              loaded={draftCanvases.length}
              total={total}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
            />
          )}

          {/* Empty State */}
          {!isLoading && draftCanvases.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="flex flex-col items-center justify-center py-12"
            >
              <div className="text-center">
                <Icon name="layout-grid" className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-sm font-semibold">No draft sessions</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Create your first session to get started.
                </p>
                <Link to="/canvas/new" className="mt-4 inline-block">
                  <Button variant="default" size="sm">
                    <Icon name="plus" className="mr-1 h-4 w-4" />
                    New Session
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          <CanvasActionModals
            modalProps={modalProps}
            modalHandlers={modalHandlers}
            onClose={closeModal}
          />
        </div>
      </div>
    </main>
  )
}
